import React, { useMemo, useState } from 'react';
import { MapPin, Search, Plus, Trash2, Database, ExternalLink, Loader2, ShieldCheck, RefreshCw, Users, MessageCircle, Clock, Ban, Link2, Pencil, FlaskConical } from 'lucide-react';
import { useProspects } from '../hooks/useProspects';
import { useCustomers } from '../hooks/useCustomers';
import ProspectSendPanel from '../components/ProspectSendPanel';
import {
  PROSPECT_STATUSES, WHATSAPP_STATES, SEARCH_STATUS_LABEL,
  customerKeySet, isExistingCustomer, filterProspects, prospectSummary, isStaleSearch, STALE_SEARCH_MINUTES, linkCandidates
} from '../utils/prospects';

const inputClass = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green';

const WHATSAPP_STYLE = {
  yes: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  no: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  unknown: 'bg-seryi-bg-dark text-seryi-text border-seryi-bg-light'
};

const SEARCH_STYLE = {
  pendiente: 'text-amber-300',
  procesando: 'text-sky-300',
  listo: 'text-emerald-300',
  error: 'text-rose-300'
};

function SearchForm({ onSubmit, disabled }) {
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('');
  const [maxResults, setMaxResults] = useState(20);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit({ query, city, maxResults });
    setBusy(false);
    if (ok) setQuery('');
  };

  return (
    <form onSubmit={submit} className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 grid grid-cols-1 md:grid-cols-[1fr_1fr_110px_auto] gap-3 items-end">
      <div className="space-y-1">
        <label htmlFor="prospect-query" className="text-[10px] uppercase font-bold text-seryi-text">Rubro</label>
        <input id="prospect-query" className={inputClass} placeholder="barberías, clínicas dentales…" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>
      <div className="space-y-1">
        <label htmlFor="prospect-city" className="text-[10px] uppercase font-bold text-seryi-text">Ciudad</label>
        <input id="prospect-city" className={inputClass} placeholder="Medellín" value={city} onChange={(e) => setCity(e.target.value)} />
      </div>
      <div className="space-y-1">
        <label htmlFor="prospect-max" className="text-[10px] uppercase font-bold text-seryi-text">Máximo</label>
        <input id="prospect-max" type="number" min="1" max="60" className={inputClass} value={maxResults} onChange={(e) => setMaxResults(e.target.value)} />
      </div>
      <button
        type="submit" disabled={disabled || busy}
        className="px-4 py-2.5 bg-seryi-green text-seryi-bg-dark hover:brightness-110 rounded-2xl text-xs font-extrabold flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <Search size={15} /> Buscar en Google Maps
      </button>
    </form>
  );
}

function ManualForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({ name: '', phone: '', category: '', city: '', address: '', notes: '', botMode: true });
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (await onSubmit(form)) onCancel();
  };

  return (
    <form onSubmit={submit} className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-5 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
      <div className="space-y-1">
        <label htmlFor="pm-name" className="text-[10px] uppercase font-bold text-seryi-text">Negocio</label>
        <input id="pm-name" className={inputClass} value={form.name} onChange={set('name')} />
      </div>
      <div className="space-y-1">
        <label htmlFor="pm-phone" className="text-[10px] uppercase font-bold text-seryi-text">Teléfono</label>
        <input id="pm-phone" className={inputClass} placeholder="300 123 4567" value={form.phone} onChange={set('phone')} />
      </div>
      <div className="space-y-1">
        <label htmlFor="pm-category" className="text-[10px] uppercase font-bold text-seryi-text">Rubro</label>
        <input id="pm-category" className={inputClass} value={form.category} onChange={set('category')} />
      </div>
      <div className="space-y-1">
        <label htmlFor="pm-city" className="text-[10px] uppercase font-bold text-seryi-text">Ciudad</label>
        <input id="pm-city" className={inputClass} value={form.city} onChange={set('city')} />
      </div>
      <div className="space-y-1 md:col-span-2">
        <label htmlFor="pm-notes" className="text-[10px] uppercase font-bold text-seryi-text">Notas</label>
        <input id="pm-notes" className={inputClass} value={form.notes} onChange={set('notes')} />
      </div>
      <div className="md:col-span-3 space-y-1">
        <label htmlFor="pm-bot" className="flex items-start gap-2 text-xs text-seryi-heading cursor-pointer">
          <input
            id="pm-bot" type="checkbox" className="mt-0.5 accent-emerald-400" checked={form.botMode}
            onChange={(e) => setForm(prev => ({ ...prev, botMode: e.target.checked }))}
          />
          <span><strong>Ya hablé con esta persona o me escribió: que el bot la atienda como prospecto</strong></span>
        </label>
        <p className="text-[10px] text-seryi-text/80 pl-6">
          Se guarda como “Contactado” y el bot le responde como vendedora desde su próximo mensaje (si no, lo atiende como cliente normal y le ofrece citas).
          No entra a la cola de envío. Desmárcala si aún no le has escrito y quieres que reciba el primer mensaje desde la pestaña Envío (necesita el celular verificado).
        </p>
      </div>
      <div className="md:col-span-3 flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-xs font-bold text-seryi-text hover:text-seryi-heading">Cancelar</button>
        <button type="submit" className="px-4 py-2 bg-seryi-green text-seryi-bg-dark rounded-xl text-xs font-extrabold">Guardar prospecto</button>
      </div>
    </form>
  );
}

// Respuestas que llegaron sin teléfono (@lid): el bot no sabe a qué prospecto corresponden hasta vincularlas.
// Casi siempre se vinculan solas por el nombre del perfil; aquí se resuelven las que no.
function UnidentifiedReplies({ replies, prospects, onLink, disabled }) {
  const [choice, setChoice] = useState({});
  const [busy, setBusy] = useState('');
  const candidates = useMemo(() => linkCandidates(prospects), [prospects]);
  if (!replies.length || !candidates.length) return null;

  const link = async (lid) => {
    if (!choice[lid]) return;
    setBusy(lid);
    await onLink(choice[lid], lid);
    setBusy('');
  };

  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 space-y-3">
      <div>
        <h3 className="text-xs font-extrabold text-amber-200 flex items-center gap-2 uppercase tracking-wider"><Link2 size={14} /> Respuestas sin identificar ({replies.length})</h3>
        <p className="text-[11px] text-amber-100/80 mt-1">
          WhatsApp entregó estas respuestas sin teléfono y no se pudieron reconocer solas. Mientras no las vincules, el bot las atiende como a un cliente normal.
          Elige a qué prospecto corresponde cada una.
        </p>
      </div>
      <ul className="space-y-2">
        {replies.map(r => (
          <li key={r.lid} className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-seryi-heading truncate">{r.name || 'Sin nombre de perfil'}</p>
              <p className="text-[11px] text-seryi-text truncate">“{r.lastMessage.slice(0, 140)}”</p>
              <p className="text-[10px] text-seryi-text/70">{new Date(r.sentAt).toLocaleString('es-CO', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
            </div>
            <select
              aria-label={`Prospecto de ${r.name || r.lid}`} className={`${inputClass} md:w-56`} value={choice[r.lid] || ''} disabled={disabled}
              onChange={(e) => setChoice(prev => ({ ...prev, [r.lid]: e.target.value }))}
            >
              <option value="">¿Qué prospecto es?</option>
              {candidates.map(c => <option key={c.id} value={c.id}>{c.name}{c.city ? ` · ${c.city}` : ''}</option>)}
            </select>
            <button
              type="button" onClick={() => link(r.lid)} disabled={disabled || !choice[r.lid] || busy === r.lid}
              aria-label={`Vincular a ${r.name || r.lid}`}
              className="px-4 py-2 bg-seryi-green text-seryi-bg-dark rounded-xl text-xs font-extrabold disabled:opacity-50 flex items-center justify-center gap-1.5"
            >
              {busy === r.lid ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Vincular
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Edita los datos de un prospecto. El teléfono solo se cambia mientras está en "Nuevo" (ver useProspects.editProspect).
function EditForm({ prospect, onSubmit, onCancel, isMaster = false }) {
  const [form, setForm] = useState({
    name: prospect.name || '', phone: prospect.phone || '', category: prospect.category || '',
    city: prospect.city || '', address: prospect.address || '', notes: prospect.notes || '', is_test: Boolean(prospect.is_test)
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));
  const phoneLocked = prospect.status !== 'nuevo';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit(prospect.id, form);
    setBusy(false);
    if (ok) onCancel();
  };

  return (
    <form onSubmit={submit} aria-label={`Editar ${prospect.name}`} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end p-4 bg-seryi-bg-dark/60">
      <div className="space-y-1">
        <label htmlFor={`pe-name-${prospect.id}`} className="text-[10px] uppercase font-bold text-seryi-text">Negocio</label>
        <input id={`pe-name-${prospect.id}`} className={inputClass} value={form.name} onChange={set('name')} />
      </div>
      <div className="space-y-1">
        <label htmlFor={`pe-phone-${prospect.id}`} className="text-[10px] uppercase font-bold text-seryi-text">Teléfono</label>
        <input id={`pe-phone-${prospect.id}`} className={`${inputClass} disabled:opacity-50`} value={form.phone} onChange={set('phone')} disabled={phoneLocked} />
        <p className="text-[10px] text-seryi-text/80">
          {phoneLocked
            ? 'No se puede cambiar: ya se le escribió o pidió no ser contactado (así no se pierde su historial ni su baja).'
            : 'Si lo cambias, el WhatsApp vuelve a “sin verificar”.'}
        </p>
      </div>
      <div className="space-y-1">
        <label htmlFor={`pe-category-${prospect.id}`} className="text-[10px] uppercase font-bold text-seryi-text">Rubro</label>
        <input id={`pe-category-${prospect.id}`} className={inputClass} value={form.category} onChange={set('category')} />
      </div>
      <div className="space-y-1">
        <label htmlFor={`pe-city-${prospect.id}`} className="text-[10px] uppercase font-bold text-seryi-text">Ciudad</label>
        <input id={`pe-city-${prospect.id}`} className={inputClass} value={form.city} onChange={set('city')} />
      </div>
      <div className="space-y-1">
        <label htmlFor={`pe-address-${prospect.id}`} className="text-[10px] uppercase font-bold text-seryi-text">Dirección</label>
        <input id={`pe-address-${prospect.id}`} className={inputClass} value={form.address} onChange={set('address')} />
      </div>
      <div className="space-y-1">
        <label htmlFor={`pe-notes-${prospect.id}`} className="text-[10px] uppercase font-bold text-seryi-text">Notas</label>
        <input id={`pe-notes-${prospect.id}`} className={inputClass} value={form.notes} onChange={set('notes')} />
      </div>
      {isMaster && (
        <div className="md:col-span-3 space-y-1">
          <label htmlFor={`pe-test-${prospect.id}`} className="flex items-start gap-2 text-xs text-seryi-heading cursor-pointer">
            <input
              id={`pe-test-${prospect.id}`} type="checkbox" className="mt-0.5 accent-amber-400" checked={form.is_test}
              onChange={(e) => setForm(prev => ({ ...prev, is_test: e.target.checked }))}
            />
            <span><strong>Número de pruebas</strong></span>
          </label>
          <p className="text-[10px] text-seryi-text/80 pl-6">
            El bot le responde siempre: sin el límite de 6 respuestas en 24 h, sin silencio por mensajes repetidos y sin quedar bloqueado por una baja (si escribe BAJA se cierra, y al volver a escribir se le atiende de nuevo). Solo lo ve el administrador.
          </p>
        </div>
      )}
      <div className="md:col-span-3 flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-xs font-bold text-seryi-text hover:text-seryi-heading">Cancelar</button>
        <button type="submit" disabled={busy} className="px-4 py-2 bg-seryi-green text-seryi-bg-dark rounded-xl text-xs font-extrabold disabled:opacity-60 flex items-center gap-1.5">
          {busy && <Loader2 size={13} className="animate-spin" />} Guardar cambios
        </button>
      </div>
    </form>
  );
}

function SearchesList({ searches }) {
  if (searches.length === 0) return null;
  return (
    <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl p-4 space-y-2">
      <p className="text-[10px] uppercase font-bold text-seryi-text tracking-wider">Búsquedas recientes</p>
      <ul className="divide-y divide-seryi-bg-light/50 text-xs">
        {searches.slice(0, 5).map(s => (
          <li key={s.id} className="py-2 flex flex-wrap items-center justify-between gap-2">
            <span className="text-seryi-heading font-semibold">{s.query} · {s.city}</span>
            <span className={`flex items-center gap-1.5 font-bold ${SEARCH_STYLE[s.status] || 'text-seryi-text'}`}>
              {(s.status === 'pendiente' || s.status === 'procesando') && <Loader2 size={12} className="animate-spin" />}
              {SEARCH_STATUS_LABEL[s.status] || s.status}
              {s.status === 'listo' && <span className="text-seryi-text font-normal">· {s.found_count} negocios nuevos</span>}
            </span>
            {s.status === 'error' && s.error && <p className="basis-full text-[11px] text-rose-300/90">{s.error}</p>}
            {s.status === 'listo' && s.error && <p className="basis-full text-[11px] text-amber-300/90">{s.error}</p>}
            {isStaleSearch(s) && (
              <p className="basis-full text-[11px] text-amber-300/90">
                Lleva más de {STALE_SEARCH_MINUTES} minutos en cola: revisa que el flujo de prospección de n8n esté activo y tenga la clave de Google Places.
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ProspectingPage() {
  const {
    prospects, searches, loading, tableMissing, isReadOnlyDemo, isMaster = false, hasActiveSearch,
    unidentified = [], reload, createSearch, addProspect, updateProspect, editProspect, deleteProspect, linkLid
  } = useProspects();
  const { customers } = useCustomers();
  const [text, setText] = useState('');
  const [status, setStatus] = useState('all');
  const [whatsapp, setWhatsapp] = useState('all');
  const [showManual, setShowManual] = useState(false);
  const [tab, setTab] = useState('prospectos'); // 'prospectos' | 'envio'
  const [editingId, setEditingId] = useState(null);

  const keys = useMemo(() => customerKeySet(customers), [customers]);
  const summary = useMemo(() => prospectSummary(prospects, keys), [prospects, keys]);
  const visible = useMemo(() => filterProspects(prospects, { text, status, whatsapp }), [prospects, text, status, whatsapp]);
  const disabled = tableMissing || isReadOnlyDemo;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-seryi-bg-light/80 pb-4">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2"><MapPin size={20} className="text-seryi-green" /> Prospección</h2>
          <p className="text-xs text-seryi-text">Encuentra negocios en Google Maps y revisa cuáles tienen WhatsApp para ofrecerles tu servicio.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => reload()} aria-label="Actualizar"
            className="px-3.5 py-2.5 bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading border border-seryi-bg-light rounded-2xl text-xs font-bold flex items-center gap-2"
          >
            <RefreshCw size={15} /> Actualizar
          </button>
          <button
            onClick={() => setShowManual(v => !v)} disabled={disabled}
            className="px-4 py-2.5 bg-seryi-bg-dark text-seryi-heading hover:border-seryi-green border border-seryi-bg-light rounded-2xl text-xs font-extrabold flex items-center gap-2 disabled:opacity-50"
          >
            <Plus size={16} /> Agregar a mano
          </button>
        </div>
      </div>

      <div className="flex items-start gap-3 bg-sky-500/10 border border-sky-500/30 text-sky-200 rounded-2xl p-4 text-xs">
        <ShieldCheck size={18} className="shrink-0 mt-0.5" />
        <p>
          Buscar y revisar <strong>no envía ningún mensaje</strong>. Solo salen mensajes cuando tú apruebas un lote en la pestaña <strong>Envío</strong>,
          y cada prospecto recibe uno solo. Quien marques como “No contactar” se conserva en la lista y nunca se le escribirá.
        </p>
      </div>

      <div className="flex items-center gap-2" role="tablist">
        {[['prospectos', 'Prospectos'], ['envio', 'Envío']].map(([key, label]) => (
          <button
            key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
            className={`px-5 py-2 rounded-2xl text-xs font-bold transition-all ${tab === key ? 'bg-seryi-green text-seryi-bg-dark shadow-md shadow-seryi-green/20' : 'bg-seryi-bg text-seryi-text hover:text-seryi-heading hover:bg-seryi-bg-light'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tableMissing && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-2xl p-4 text-xs">
          <Database size={18} className="shrink-0 mt-0.5" />
          <p>Falta crear las tablas de prospección en Supabase. Ejecuta <code className="font-mono">supabase/migrations/20260930g_prospecting.sql</code> en el SQL Editor y recarga esta página.</p>
        </div>
      )}

      {tab === 'envio' ? (
        <ProspectSendPanel prospects={prospects} keys={keys} />
      ) : (
        <>
          <SearchForm onSubmit={createSearch} disabled={disabled} />
          {showManual && !disabled && <ManualForm onSubmit={addProspect} onCancel={() => setShowManual(false)} />}
          <UnidentifiedReplies replies={unidentified} prospects={prospects} onLink={linkLid} disabled={disabled} />
          <SearchesList searches={searches} />
          {hasActiveSearch && <p className="text-[11px] text-seryi-text -mt-3">Hay una búsqueda en curso: esta lista se actualiza sola cuando termine.</p>}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ['Total', summary.total, Users, 'text-seryi-heading'],
              ['Con WhatsApp', summary.withWhatsapp, MessageCircle, 'text-emerald-300'],
              ['Por verificar', summary.toVerify, Clock, 'text-amber-300'],
              ['Listos para contactar', summary.contactable, ShieldCheck, 'text-sky-300'],
              ['No contactar', summary.optedOut, Ban, 'text-rose-300']
            ].map(([label, value, Icon, color]) => (
              <div key={label} className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4">
                <div className="flex items-center justify-between text-seryi-text">
                  <span className="text-[10px] uppercase font-bold tracking-wider">{label}</span>
                  <Icon size={15} className={color} />
                </div>
                <p className={`text-2xl font-black mt-1 ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {[{ key: 'all', label: 'Todos' }, ...PROSPECT_STATUSES].map(s => (
                <button
                  key={s.key} onClick={() => setStatus(s.key)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${status === s.key ? 'bg-seryi-green text-seryi-bg-dark' : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <select aria-label="Filtrar por WhatsApp" className={`${inputClass} md:w-44`} value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}>
                <option value="all">WhatsApp: todos</option>
                {WHATSAPP_STATES.map(w => <option key={w.key} value={w.key}>{w.label}</option>)}
              </select>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3.5 top-2.5 text-seryi-text" size={15} />
                <input
                  type="text" placeholder="Buscar negocio, ciudad o teléfono..." className={`${inputClass} pl-9`}
                  value={text} onChange={(e) => setText(e.target.value)}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <p className="text-xs text-seryi-text py-10 text-center">Cargando prospectos…</p>
          ) : visible.length === 0 ? (
            <div className="text-center py-14 bg-seryi-bg border border-seryi-bg-light rounded-3xl">
              <MapPin size={28} className="mx-auto text-seryi-text mb-3" />
              <p className="text-sm font-bold text-seryi-heading">{prospects.length === 0 ? 'Aún no hay prospectos' : 'Nada coincide con tu filtro'}</p>
              {prospects.length === 0 && !tableMissing && (
                <p className="text-xs text-seryi-text mt-1 max-w-sm mx-auto">Haz una búsqueda por rubro y ciudad, o agrega un negocio a mano.</p>
              )}
            </div>
          ) : (
            <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[760px]">
                <thead>
                  <tr className="border-b border-seryi-bg-light text-[10px] uppercase font-bold text-seryi-text tracking-wider">
                    <th className="py-3 px-4">Negocio</th>
                    <th className="py-3 px-4">Teléfono</th>
                    <th className="py-3 px-4">WhatsApp</th>
                    <th className="py-3 px-4">Estado</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-seryi-bg-light/50 text-xs">
                  {visible.map(p => {
                    const isCustomer = isExistingCustomer(p, keys);
                    return (
                      <React.Fragment key={p.id}>
                      <tr>
                        <td className="py-3.5 px-4">
                          <p className="font-bold text-seryi-heading">
                            {p.name}
                            {p.is_test && <span title="Número de pruebas: el bot le responde sin límites" className="ml-2 inline-flex items-center gap-1 align-middle px-2 py-0.5 rounded-full border text-[10px] font-bold bg-amber-500/10 text-amber-300 border-amber-500/30"><FlaskConical size={10} /> Prueba</span>}
                          </p>
                          <p className="text-[11px] text-seryi-text">{[p.category, p.city].filter(Boolean).join(' · ') || '—'}</p>
                          {p.address && <p className="text-[11px] text-seryi-text/70">{p.address}</p>}
                          {p.rating != null && <p className="text-[11px] text-amber-300">★ {p.rating}{p.reviews_count ? ` (${p.reviews_count})` : ''}</p>}
                        </td>
                        <td className="py-3.5 px-4 font-mono text-seryi-text">{p.phone || 'Sin teléfono'}</td>
                        <td className="py-3.5 px-4">
                          <span className={`inline-block px-2.5 py-1 rounded-full border text-[10px] font-bold ${WHATSAPP_STYLE[p.whatsapp] || WHATSAPP_STYLE.unknown}`}>
                            {WHATSAPP_STATES.find(w => w.key === p.whatsapp)?.label || 'Sin verificar'}
                          </span>
                          {isCustomer && <span className="ml-1.5 inline-block px-2.5 py-1 rounded-full border text-[10px] font-bold bg-violet-500/10 text-violet-300 border-violet-500/30">Ya es cliente</span>}
                        </td>
                        <td className="py-3.5 px-4">
                          <select
                            aria-label={`Estado de ${p.name}`} className={`${inputClass} w-36`} value={p.status} disabled={isReadOnlyDemo}
                            onChange={(e) => updateProspect(p.id, { status: e.target.value })}
                          >
                            {PROSPECT_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                          </select>
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setEditingId(editingId === p.id ? null : p.id)}
                              disabled={isReadOnlyDemo} aria-label={`Editar ${p.name}`} className="p-2 text-seryi-text hover:text-seryi-green disabled:opacity-50"
                            >
                              <Pencil size={15} />
                            </button>
                            {p.maps_url && (
                              <a href={p.maps_url} target="_blank" rel="noreferrer" aria-label={`Ver ${p.name} en Google Maps`} className="p-2 text-seryi-text hover:text-seryi-green">
                                <ExternalLink size={15} />
                              </a>
                            )}
                            {p.status !== 'no_contactar' && (
                              <button
                                onClick={() => window.confirm(`¿Eliminar a ${p.name} de la lista?`) && deleteProspect(p.id)}
                                disabled={isReadOnlyDemo} aria-label={`Eliminar ${p.name}`} className="p-2 text-seryi-text hover:text-rose-400 disabled:opacity-50"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {editingId === p.id && (
                        <tr><td colSpan={5} className="p-0"><EditForm prospect={p} onSubmit={editProspect} onCancel={() => setEditingId(null)} isMaster={isMaster} /></td></tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
